import { useCallback, useEffect, useRef, useState } from "react";
import { useLazyVehicleMapDataMultiEventCountQuery } from "../../api/network/adminApiServices";
import { VehicleMapMultiEventCountResponse } from "../../api/types/VehicleMap";

// Every checkbox click changes the selection, and a select-all changes it
// once per fleet. Counting after the clicking settles keeps that to one
// request instead of one per box.
const COUNT_DEBOUNCE_MS = 350;

export type MultiVehicleEventCount = {
  // what the cap is measured against - null until a count has come back
  eventCount: number | null;
  maxEvents: number | null;
  isOverLimit: boolean;
  isCounting: boolean;
  isError: boolean;
  // why the count could not be had, in the server's own words
  errorMessage: string | null;
  byDevice: VehicleMapMultiEventCountResponse["event_count_by_device"] | null;
};

// What the server said went wrong. A count that fails is not the same thing
// as a selection that is too heavy, and the two were indistinguishable while
// every failure read "Event count unavailable" - a refused device count, an
// expired session and a query that timed out all looked identical.
const readCountError = (error: unknown): string | null => {
  const data = (error as { data?: { error?: string } } | undefined)?.data;
  if (typeof data?.error === "string" && data.error) return data.error;
  const status = (error as { status?: number | string } | undefined)?.status;
  return status === undefined ? null : `Request failed (${status})`;
};

// How heavy the picked vehicles and day are, asked before the map request
// goes out. The plot endpoint refuses an over-cap selection outright, so
// without this the only way to find out is to pick, wait, and be told no.
export const useMultiVehicleEventCount = ({
  deviceIds,
  startTimestamp,
  endTimestamp,
}: {
  deviceIds: string[];
  startTimestamp: number;
  endTimestamp: number;
}): MultiVehicleEventCount => {
  const [fetchEventCount] = useLazyVehicleMapDataMultiEventCountQuery();
  const [result, setResult] =
    useState<VehicleMapMultiEventCountResponse | null>(null);
  const [isCounting, setIsCounting] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // only the newest run may publish - a selection changed mid-flight would
  // otherwise be described by the count for the selection before it
  const runIdRef = useRef(0);

  // the selection is a new array on every render; the ids in it are what
  // actually decides whether a recount is needed
  const deviceIdSignature = [...deviceIds].sort().join(",");

  const count = useCallback(async (runId: number) => {
    if (!deviceIdSignature || !startTimestamp || !endTimestamp) {
      setIsCounting(false);
      return;
    }

    try {
      const data = await fetchEventCount({
        device_ids: deviceIdSignature.split(","),
        start_timestamp: startTimestamp,
        end_timestamp: endTimestamp,
      }).unwrap();
      if (runId !== runIdRef.current) return;
      setResult(data);
      setIsError(false);
      setErrorMessage(null);
    } catch (error) {
      if (runId !== runIdRef.current) return;
      // a count that can't be had shouldn't block plotting - the map
      // request still enforces the cap on its own
      setResult(null);
      setIsError(true);
      setErrorMessage(readCountError(error));
    }
    setIsCounting(false);
  }, [deviceIdSignature, startTimestamp, endTimestamp, fetchEventCount]);

  useEffect(() => {
    // Invalidate the previous request immediately. Waiting until the debounce
    // fires would let an old response repopulate `result` for the new inputs.
    const runId = ++runIdRef.current;
    setResult(null);
    setIsError(false);
    setErrorMessage(null);

    if (!deviceIdSignature || !startTimestamp || !endTimestamp) {
      setIsCounting(false);
      return;
    }
    setIsCounting(true);
    const timer = setTimeout(() => count(runId), COUNT_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      if (runIdRef.current === runId) ++runIdRef.current;
    };
  }, [count, deviceIdSignature, startTimestamp, endTimestamp]);

  return {
    eventCount: result?.budget_event_count ?? null,
    maxEvents: result?.max_events ?? null,
    // strictly what the server said - an unanswered count is not a refusal
    isOverLimit: !!result && !result.within_limit,
    isCounting,
    isError,
    errorMessage,
    byDevice: result?.event_count_by_device ?? null,
  };
};
